import type { ClientLoaderFunctionArgs } from 'react-router'
import { getI18n } from 'src/i18n/i18n.middleware'
import { dynamic } from 'src/utils/ssr'
import { SITE_NAME, staticMeta, staticMetaLoader } from 'src/utils/ssr-meta/seo'
import type { Route } from './+types/_app.recap.($year)'

const getClientModule = () => import('src/clientRoutes/_app.recap.($year)')

export const handle = {
  name: 'year-recap',
  track: { scene: 'event', section: 'year-recap-2025' },
}

/** SSR loader. Source: https://github.com/troph-team/pixai/blob/main/packages/pixai-server/src/ssr/router.ts#L494-L514 */
export function loader({ params, request, context }: Route.LoaderArgs) {
  const { t, exists } = getI18n(context)
  const year = params.year || '2025'

  const titleKey = 'yearrecap:label'
  const descKey = `yearrecap:${year}.banners.daily-claim-bottom.label`
  const imageKey = `yearrecap:${year}.og.image`

  const title = exists(titleKey) ? t(titleKey) : `Year Recap ${year}`
  return staticMetaLoader({
    requestUrl: request.url,
    title: `${title} | ${SITE_NAME}`,
    description: exists(descKey) ? t(descKey) : '',
    image: exists(imageKey) ? t(imageKey) : undefined,
  })
}

/** Meta function for SSR SEO. */
export function meta({ loaderData }: Route.MetaArgs) {
  if (loaderData && typeof loaderData === 'object' && '_meta' in loaderData) {
    return staticMeta(loaderData as Awaited<ReturnType<typeof loader>>)
  }
  return staticMeta(null)
}

// Pattern B: client module returns data ({ year }) needed by the page component,
// so we can't use createHydratingClientLoader (which discards return values).
export async function clientLoader({
  serverLoader,
  ...args
}: ClientLoaderFunctionArgs) {
  const { loader } = await getClientModule()
  // Client loader throws redirect for unauthenticated users,
  // or returns { year: 2025 } for the page component.
  const result = await loader(args)
  // If client loader returned data (not a redirect), merge with server data
  // so meta() still has access to _meta from the server loader.
  if (result && !(result instanceof Response)) {
    // serverLoader is only available in SSR framework mode, not in CSR data-router
    const serverData =
      await serverLoader?.<Awaited<ReturnType<typeof loader>>>()
    return { ...serverData, ...result }
  }
  return serverLoader?.() ?? result
}
clientLoader.hydrate = true

export default dynamic(getClientModule, { ssr: false })
